import { Link, useForm } from '@inertiajs/react'
import { useState } from 'react'
import { formatLempiras } from '../../lib/format'
import { useCart, CartProvider, Cart } from '../../lib/cart'
import CartHeader from '../../lib/CartHeader'

interface Account { id: number; name: string; slug: string; logo_url?: string | null; shipping_cost_cents: number; accept_transfer: boolean; accept_card: boolean; tenant_type?: string; loyalty_points_enabled?: boolean }
interface Branch { id: number; name: string; address?: string; phone?: string }
interface Customer { id: number; name: string; email: string; phone: string; tax_id: string }
interface Guest { name?: string; email?: string; phone?: string; dni?: string }
interface Props { account: Account; branches: Branch[]; customer?: Customer | null; guest?: Guest | null; cart?: Cart; has_bank_accounts?: boolean; last_delivery?: { delivery_address: string; delivery_city: string; delivery_phone: string } | null }

export default function CatalogCheckout(props: Props) {
  const { account, cart } = props
  return (
    <CartProvider cart={cart} slug={account.slug}>
      <CatalogCheckoutInner {...props} />
    </CartProvider>
  )
}

function CatalogCheckoutInner({ account, branches, customer, guest, has_bank_accounts, last_delivery }: { account: Account; branches: Branch[]; customer?: Customer | null; guest?: Guest | null; has_bank_accounts?: boolean; last_delivery?: { delivery_address: string; delivery_city: string; delivery_phone: string } | null }) {
  const { items, count } = useCart()
  const subtotalCents = items.reduce((sum, i) => sum + (i.unit_price_cents * i.quantity), 0)
  const discountCents = items.reduce((sum, i) => sum + (i.discount_cents ?? 0), 0)
  const taxCents = items.reduce((s, i) => {
    const rate = i.tax_rate ?? 15
    const lineSubtotal = i.unit_price_cents * i.quantity
    const lineDiscounted = lineSubtotal - (i.discount_cents ?? 0)
    return s + (lineSubtotal > 0 ? Math.round(lineDiscounted * (rate / 100)) : 0)
  }, 0)
  const [wantSignup, setWantSignup] = useState(true)

  const transferAvailable = account.accept_transfer && has_bank_accounts
  const cardAvailable = account.accept_card

  const { data, setData, post, processing, errors } = useForm({
    fulfillment_type: 'pickup',
    branch_id: branches[0]?.id || '',
    delivery_address: last_delivery?.delivery_address || '',
    delivery_city: last_delivery?.delivery_city || '',
    delivery_phone: last_delivery?.delivery_phone || '',
    delivery_notes: '',
    payment_method: transferAvailable ? 'transfer' : cardAvailable ? 'link_request' : 'transfer',
    notes: '',
    guest_name: customer?.name || guest?.name || '',
    guest_email: customer?.email || guest?.email || '',
    guest_phone: customer?.phone || guest?.phone || '',
    guest_dni: customer?.tax_id || guest?.dni || '',
    guest_birthdate: '',

    signup_after: '1',
  })

  const shippingCostCents = account.shipping_cost_cents ?? 0
  const shippingCostCentsEffective = data.fulfillment_type === 'delivery' ? shippingCostCents : 0
  const totalCentsFinal = subtotalCents - discountCents + taxCents + shippingCostCentsEffective

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // Sync signup_after with the checkbox state
    setData('signup_after', wantSignup ? '1' : '0')
    post(`/${account.slug}/catalog/checkout`)
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CartHeader slug={account.slug} accountName={account.name} logoUrl={account.logo_url} />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <p className="text-gray-500 text-lg mb-3">Tu carrito está vacío</p>
          <Link href={`/${account.slug}/catalog`} className="text-[#55ACEE] hover:underline">Ver catálogo</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CartHeader slug={account.slug} accountName={account.name} logoUrl={account.logo_url} />

      <div className="max-w-4xl mx-auto px-4 py-6">
        <Link href={`/${account.slug}/catalog`} className="text-[#55ACEE] hover:underline text-sm mb-4 inline-block">
          ← Volver al catálogo
        </Link>
        <h1 className="text-2xl font-bold text-[#202020] mb-6">Registro de ventas</h1>

        {errors && Object.keys(errors).length > 0 && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4">
            {Object.entries(errors).map(([k, v], i) => (
              <p key={i}>{k}: {Array.isArray(v) ? v.join(', ') : v}</p>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit} className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 space-y-4">
            {/* Fulfillment */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h2 className="font-semibold text-[#202020] mb-3">¿Cómo lo recibes?</h2>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input type="radio" value="pickup" checked={data.fulfillment_type === 'pickup'} onChange={(e) => setData('fulfillment_type', e.target.value)} />
                  <span>{account.tenant_type === 'restaurante' ? 'Ir al local' : 'Recoger en tienda'}</span>
                </label>
                <label className="flex items-center gap-2">
                  <input type="radio" value="delivery" checked={data.fulfillment_type === 'delivery'} onChange={(e) => setData('fulfillment_type', e.target.value)} />
                  <span>Envío a domicilio</span>
                </label>
              </div>
              {data.fulfillment_type === 'pickup' ? (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal de recogida</label>
                  <select value={data.branch_id} onChange={(e) => setData('branch_id', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded" required>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>{b.name} — {b.address}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  <input type="text" placeholder="Dirección" value={data.delivery_address} onChange={(e) => setData('delivery_address', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded" required />
                  <input type="text" placeholder="Ciudad" value={data.delivery_city} onChange={(e) => setData('delivery_city', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded" />
                  <input type="text" placeholder="Teléfono de contacto" value={data.delivery_phone} onChange={(e) => setData('delivery_phone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded" />
                </div>
              )}
            </div>

            {/* Datos del cliente */}
            <div className="bg-white p-4 rounded-lg shadow">
              <h2 className="font-semibold text-[#202020] mb-3">
                Tus datos {customer && <span className="text-xs text-green-600">(logueado)</span>}
              </h2>

              {/* Signup checkbox — moved to TOP, before fields */}
              {!customer && (
                <label className="flex items-center gap-2 mb-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={wantSignup} onChange={(e) => setWantSignup(e.target.checked)} />
                  <span>{account.loyalty_points_enabled ? 'Crear cuenta para ver mis pedidos y acumular puntos' : 'Crear cuenta para ver mis pedidos'}</span>
                </label>
              )}
              {!customer && wantSignup && (
                <p className="text-xs text-gray-500 mb-3">
                  📧 Te enviaremos un correo para crear tu contraseña y ver tus pedidos.
                </p>
              )}

              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="Nombre completo *" value={data.guest_name}
                  onChange={(e) => setData('guest_name', e.target.value)}
                  readOnly={!!customer}
                  className="col-span-2 px-3 py-2 border border-gray-300 rounded disabled:bg-gray-100" required />
                <input type="tel" placeholder="Teléfono *" value={data.guest_phone}
                  onChange={(e) => setData('guest_phone', e.target.value)}
                  readOnly={!!customer}
                  className="col-span-2 px-3 py-2 border border-gray-300 rounded disabled:bg-gray-100" required />
                <input type="text" placeholder="DNI / Identidad *" value={data.guest_dni}
                  onChange={(e) => setData('guest_dni', e.target.value)}
                  readOnly={!!customer}
                  className="px-3 py-2 border border-gray-300 rounded disabled:bg-gray-100" required />
                <input type="email" placeholder="Email *" value={data.guest_email}
                  onChange={(e) => setData('guest_email', e.target.value)}
                  readOnly={!!customer}
                  className="px-3 py-2 border border-gray-300 rounded disabled:bg-gray-100" required />

                {/* Registration-only fields — only when signup is enabled */}
                {(!customer && wantSignup) && (
                  <>
                    <div className="col-span-2">
                      <input type="date" value={data.guest_birthdate}
                        onChange={(e) => setData('guest_birthdate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded" />
                      <p className="text-xs text-gray-400 mt-1">Fecha de nacimiento</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Payment method */}
            {transferAvailable || cardAvailable ? (
            <div className="bg-white p-4 rounded-lg shadow">
              <h2 className="font-semibold text-[#202020] mb-3">Método de pago</h2>
              <div className="space-y-2">
                {transferAvailable && (
                <label className="flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                  <input type="radio" value="transfer" checked={data.payment_method === 'transfer'}
                    onChange={(e) => setData('payment_method', e.target.value)} />
                  <div>
                    <p className="font-medium">Transferencia bancaria</p>
                    <p className="text-xs text-gray-500">Confirma el pedido para ver los datos para la transferencia y adjuntar tu comprobante de pago.</p>
                  </div>
                </label>
                )}
                {cardAvailable && (
                <label className="flex items-start gap-2 p-2 border rounded cursor-pointer hover:bg-gray-50">
                  <input type="radio" value="link_request" checked={data.payment_method === 'link_request'}
                    onChange={(e) => setData('payment_method', e.target.value)} />
                  <div>
                    <p className="font-medium">Link de Pago</p>
                    <p className="text-xs text-gray-500">El comercio te enviará un link para pagar con tarjeta.</p>
                  </div>
                </label>
                )}
              </div>
            </div>
            ) : null}
          </div>

          {/* Summary sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white p-4 rounded-lg shadow sticky top-20">
              <h2 className="font-semibold text-[#202020] mb-3">Resumen</h2>
              <div className="space-y-1 text-sm">
                {items.map((i, idx) => (
                  <div key={idx} className="flex justify-between text-gray-600">
                    <span className="truncate">{i.quantity}× {i.product_name}</span>
                    <span>{formatLempiras(i.unit_price_cents * i.quantity - (i.discount_cents ?? 0))}</span>
                  </div>
                ))}
              </div>
              <div className="border-t mt-3 pt-3 space-y-1">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span>{formatLempiras(subtotalCents)}</span></div>
                {discountCents > 0 && (
                  <div className="flex justify-between text-green-600 font-medium"><span>Descuento promociones</span><span>−{formatLempiras(discountCents)}</span></div>
                )}
                <div className="flex justify-between text-gray-600"><span>ISV</span><span>{formatLempiras(taxCents)}</span></div>
                {shippingCostCentsEffective > 0 && (
                  <div className="flex justify-between text-gray-600"><span>Envío</span><span>{formatLempiras(shippingCostCentsEffective)}</span></div>
                )}
                <div className="flex justify-between text-lg font-bold text-[#202020] pt-2 border-t"><span>Total ({count})</span><span>{formatLempiras(totalCentsFinal)}</span></div>
              </div>
              <button type="submit" disabled={processing}
                className="w-full bg-[#FFC107] text-[#202020] py-3 rounded font-semibold hover:bg-[#e6ad06] mt-4 disabled:opacity-50">
                {processing ? 'Procesando...' : 'Confirmar Pedido'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
